/**
 * Analytics
 *
 * - Injects Google Analytics 4 (gtag.js) on mount when
 *   `REACT_APP_GA_MEASUREMENT_ID` is defined at build time.
 * - Fires a `page_view` event on every React Router navigation.
 * - Injects a Google Search Console site-verification meta tag when
 *   `REACT_APP_GSC_VERIFICATION` is defined.
 *
 * Both env vars are optional — nothing loads if they're unset, which keeps
 * dev and staging environments free of production tracking.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
const GSC_TOKEN = process.env.REACT_APP_GSC_VERIFICATION || '';

let gaBooted = false;
function bootGA() {
  if (gaBooted || !GA_ID || typeof window === 'undefined') return;
  gaBooted = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag(...args) { window.dataLayer.push(args); }
  window.gtag = gtag;
  gtag('js', new Date());
  // send_page_view=false so we can fire it manually on route change
  gtag('config', GA_ID, { send_page_view: false });
}

function setGSC() {
  if (!GSC_TOKEN || typeof document === 'undefined') return;
  let el = document.querySelector('meta[name="google-site-verification"]');
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'google-site-verification');
    document.head.appendChild(el);
  }
  el.setAttribute('content', GSC_TOKEN);
}

const Analytics = () => {
  const location = useLocation();

  useEffect(() => {
    bootGA();
    setGSC();
  }, []);

  useEffect(() => {
    if (!GA_ID || !window.gtag) return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);

  return null;
};

export default Analytics;
